import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  useMediaQuery,
  Box,
  Grid,
} from '@mui/material'
import MainHeader from '../shared/organism/MainHeader'
import React, { useState } from 'react'
import AccSecurity from '../shared/molecule/AccSecurity'
import { theme } from '../../theme/DashboardTheme'
import { ExpandMore } from '@mui/icons-material'
import PageLayout from './PageLayout'
import CommunicationPreference from '../Views/SettingsViews/CommunicationPreference'
import { useLocation, useNavigate, Outlet } from 'react-router-dom'

interface TabBarProps {
  selectedTab: number
  onUpdatePreferences?: () => void
  preferenceState: { hasChanged: boolean; updating: boolean }
}

const TabBar: React.FC<TabBarProps> = ({ selectedTab, onUpdatePreferences, preferenceState }) => {
  const navigate = useNavigate()
  const tabs = [
    { label: 'Account & Security', path: '/settings/account-security' },
    { label: 'Communication Preferences', path: '/settings/communication-preferences' }
  ]

  const isActive = preferenceState.hasChanged && !preferenceState.updating

  return (
    <Grid
      container
      sx={{
        paddingTop: 1,
        marginTop: '25px',
        marginBottom: 3,
      }}>
      <Grid
        container
        item
        md={12}
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'flex-end'
        }}>
        <Grid item sm={12} md={8}>
          <Box sx={{ display: 'flex', gap: 3, paddingLeft: 0 }}>
            {tabs.map((tab, index) => (
              <Box
                key={index}
                onClick={() => navigate(tab.path)}
                sx={{
                  px: { md: 1, lg: 2 },
                  height: '65px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: index === selectedTab ? '#9135E0' : '#6B7280',
                  fontSize: { sm: 15, md: 18, lg: 20 },
                  fontFamily: index === selectedTab ? 'InterMedium' : 'InterRegular',
                  fontWeight: index === selectedTab ? 500 : 400,
                  borderBottom: index === selectedTab ? '2px solid #9135E0' : 'none',
                }}>
                {tab.label}
              </Box>
            ))}
          </Box>
        </Grid>
        {selectedTab === 1 && (
          <Grid item sm={12} md={4} mt={{ sm: 3, md: 0 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              onClick={isActive ? onUpdatePreferences : undefined}
              sx={{
                height: { sm: '45px', md: '60px' },
                borderRadius: '10px',
                background: '#9135E0',
                color: '#fff',
                fontFamily: 'InterMedium',
                fontSize: { sm: '13px', md: '14px', lg: '16px' },
                fontWeight: 500,
                maxWidth: { sm: '210px', md: '240px' },
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                opacity: isActive ? 1 : 0.6,
                cursor: isActive ? 'pointer' : 'default',
                pointerEvents: isActive ? 'auto' : 'none',
              }}>
              {preferenceState.updating ? 'Updating...' : 'Update your Preferences'}
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export const SettingsView = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const location = useLocation()
  const navigate = useNavigate()
  const [updatePreferencesFn, setUpdatePreferencesFn] = useState<(() => Promise<void>) | null>(null)
  const [preferenceState, setPreferenceState] = useState<{ hasChanged: boolean; updating: boolean }>({
    hasChanged: false,
    updating: false
  })

  const selectedTab = location.pathname.includes('communication-preferences') ? 1 : 0

  const registerUpdateFunction = (fn: () => Promise<void>) => {
    setUpdatePreferencesFn(() => fn)
  }

  const handleStateChange = (state: { hasChanged: boolean; updating: boolean }) => {
    setPreferenceState(state)
  }

  const handleUpdatePreferences = () => {
    if (updatePreferencesFn) {
      updatePreferencesFn()
    }
  }

  return (
    <PageLayout>
      <MainHeader title={'Settings'} />
      {isMobile ? (
        <div style={{ marginBlock: '1rem' }}>
          <Accordion sx={{ boxShadow: 'none' }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel1-content"
              onClick={() => navigate('/settings/account-security')}>
              Account & Security
            </AccordionSummary>
            <AccordionDetails>
              <AccSecurity />
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ boxShadow: 'none' }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel2-content"
              onClick={() => navigate('/settings/communication-preferences')}>
              Communication Preferences
            </AccordionSummary>
            <AccordionDetails>
              <CommunicationPreference
                registerUpdateFunction={registerUpdateFunction}
                onStateChange={handleStateChange}
              />
            </AccordionDetails>
          </Accordion>
        </div>
      ) : (
        <Box sx={{ mb: '20px' }}>
          <TabBar
            selectedTab={selectedTab}
            onUpdatePreferences={handleUpdatePreferences}
            preferenceState={preferenceState}
          />
          <Outlet context={{ registerUpdateFunction, onStateChange: handleStateChange }} />
        </Box>
      )}
    </PageLayout>
  )
}
